import type { RequestBody, ResponseData } from '@vrplatform/data';
import {
  type AmountFilterNumberString,
  formatAmountFilterToString,
  formatStringToAmountFilter,
} from '@vrplatform/ui-common';
import { formatCurrency } from '@vrplatform/utils';

export const FEE_AMOUNT_FILTER_DEFAULT_VALUE = 'All amounts';

type RecurringFeeAccountTotalCondition = NonNullable<
  RequestBody<'post:/recurring-fees'>['accountTotalConditions']
>[number];

type RecurringFeeAccountTotalConditionResponse = NonNullable<
  ResponseData<'get:/recurring-fees/{id}'>['accountTotalConditions']
>[number];

export const hasScopedFeeAmountFilter = (
  value: AmountFilterNumberString | null | undefined
) => Boolean(formatStringToAmountFilter(value));

export const shouldRequireFeeLinkedAccounts = ({
  rateType,
  amountFilter,
}: {
  rateType: 'flat' | 'percentage' | null | undefined;
  amountFilter: AmountFilterNumberString | null | undefined;
}) => rateType !== 'flat' || hasScopedFeeAmountFilter(amountFilter);

const getNormalizedOperator = (
  operator: RecurringFeeAccountTotalConditionResponse['operator']
): 'eq' | 'gt' | 'lt' => {
  if (operator === '=') return 'eq';
  if (operator === '>' || operator === '>=') return 'gt';
  return 'lt';
};

export const getFeeAmountFilterLabel = (
  value: AmountFilterNumberString | null | undefined
) => {
  const amount = formatStringToAmountFilter(value);

  if (!amount) return FEE_AMOUNT_FILTER_DEFAULT_VALUE;

  if (amount.type === 'eq') {
    return formatCurrency(amount.centTotal / 100);
  }

  if (amount.type === 'gt') {
    return `> ${formatCurrency(amount.centTotal / 100)}`;
  }

  if (amount.type === 'lt') {
    return `< ${formatCurrency(amount.centTotal / 100)}`;
  }

  return `${formatCurrency(amount.gtCentTotal / 100)} ~ ${formatCurrency(
    amount.ltCentTotal / 100
  )}`;
};

export const buildFeeAccountTotalConditions = ({
  amountFilter,
  linkedAccountIds,
}: {
  amountFilter: AmountFilterNumberString | null | undefined;
  linkedAccountIds: string[] | null | undefined;
}): RequestBody<'post:/recurring-fees'>['accountTotalConditions'] => {
  const amount = formatStringToAmountFilter(amountFilter);
  const accountIds = [...new Set(linkedAccountIds ?? [])];

  if (!amount || !accountIds.length) return null;

  const baseConditions: Omit<RecurringFeeAccountTotalCondition, 'accountId'>[] =
    (() => {
      if (amount.type === 'between') {
        return [
          {
            operator: '>=',
            centValue: amount.gtCentTotal,
          },
          {
            operator: '<=',
            centValue: amount.ltCentTotal,
          },
        ];
      }

      return [
        {
          operator:
            amount.type === 'eq' ? '=' : amount.type === 'gt' ? '>=' : '<=',
          centValue: amount.centTotal,
        },
      ];
    })();

  return accountIds.flatMap((accountId) =>
    baseConditions.map((condition) => ({
      accountId,
      ...condition,
    }))
  );
};

export const getFeeAmountFilterFromAccountTotalConditions = (
  accountTotalConditions:
    | ResponseData<'get:/recurring-fees/{id}'>['accountTotalConditions']
    | null
    | undefined
): AmountFilterNumberString | null => {
  if (!accountTotalConditions?.length) return null;

  const conditionKeysByAccount = new Map<string, string[]>();

  for (const condition of accountTotalConditions) {
    const normalizedOperator = getNormalizedOperator(condition.operator);
    const key = `${normalizedOperator}:${condition.centValue}`;

    conditionKeysByAccount.set(condition.accountId, [
      ...(conditionKeysByAccount.get(condition.accountId) ?? []),
      key,
    ]);
  }

  const normalizedConditionKeys = [
    ...new Set(
      accountTotalConditions.map(
        (condition) =>
          `${getNormalizedOperator(condition.operator)}:${condition.centValue}`
      )
    ),
  ].sort();

  const expectedKey = normalizedConditionKeys.join('|');

  for (const [accountId, conditionKeys] of conditionKeysByAccount) {
    const normalizedKey = [...new Set(conditionKeys)].sort().join('|');

    if (normalizedKey !== expectedKey) {
      throw new Error(
        `Recurring fee amount filter is inconsistent for account ${accountId}`
      );
    }
  }

  const normalizedConditions = normalizedConditionKeys.map((key) => {
    const [operator, centValue] = key.split(':');

    return {
      operator,
      centValue: Number(centValue),
    };
  });

  if (normalizedConditions.length === 1) {
    const [condition] = normalizedConditions;

    if (condition.operator === 'eq') {
      return formatAmountFilterToString({
        type: 'eq',
        centTotal: condition.centValue,
      });
    }

    if (condition.operator === 'gt') {
      return formatAmountFilterToString({
        type: 'gt',
        centTotal: condition.centValue,
      });
    }

    return formatAmountFilterToString({
      type: 'lt',
      centTotal: condition.centValue,
    });
  }

  if (
    normalizedConditions.length === 2 &&
    normalizedConditions.some((condition) => condition.operator === 'gt') &&
    normalizedConditions.some((condition) => condition.operator === 'lt')
  ) {
    const gtCondition = normalizedConditions.find(
      (condition) => condition.operator === 'gt'
    );
    const ltCondition = normalizedConditions.find(
      (condition) => condition.operator === 'lt'
    );

    if (!gtCondition || !ltCondition) {
      throw new Error('Recurring fee amount filter is missing range bounds');
    }

    return formatAmountFilterToString({
      type: 'between',
      centTotal: undefined,
      gtCentTotal: gtCondition.centValue,
      ltCentTotal: ltCondition.centValue,
    });
  }

  throw new Error('Recurring fee amount filter shape is not supported');
};
